import { Box, Button } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { SegmentTabs } from "../../../../../../../../components/SegmentTabs";
import Flex from "../../../../../../../../components/Flex";
import SideSheetDrawer from "../../../../../../../../components/SidesheetDrawer";
import Text from "../../../../../../../../components/Text";
import { DisclosureHost } from "../../../../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../../../../hooks/useSnackbar";
import { useResolveRequiringAction } from "../../../../../../../../queries/patient-chart/requiring-action/use-resolve-requiring-action";
import { showToastOnApiCall } from "../../../../../../../../utils/utils";
import ViewPatientIntakeForm from "../../../../intake-form/ViewPatientIntakeForm";
import { INTAKE_FORM_SECTIONS } from "../../../appointment-constant";

export type IntakeFormSection =
  | "demographics"
  | "insurance"
  | "paymentPolicy"
  | "patientMedicalHistory"
  | "medicalServicesAgreement"
  // | "HIPAA Consent"
  | "consentForServices"
  | "aiConsent"
  | "privacyPractices"
  | "releaseOfInformation"
  | "physicianPatientArbitrationAgreement";

interface IntakePreviewModalProps {
  disclosureHost: DisclosureHost;
  selectedSection?: IntakeFormSection[];
  resolveRequiringActionId?: string;
  onBack?: () => void;
  backLabel?: string;
  _showTabs?: boolean;
}

const getSectionLabel = (value: string) =>
  INTAKE_FORM_SECTIONS.find(
    (s: { label: string; value: string }) => s.value === value
  )?.label ?? value;

const IntakePreviewModal = ({
  disclosureHost,
  selectedSection,
  resolveRequiringActionId,
  onBack,
  backLabel = "Back to Appointment Details",
  _showTabs = false,
}: IntakePreviewModalProps) => {
  const { mutateAsync: resolveRequiringAction } = useResolveRequiringAction();
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();

  const submittedFormNames: IntakeFormSection[] = useMemo(() => {
    const formNameStr = disclosureHost?.data?.formName as string | undefined;
    if (!formNameStr) return [];
    return formNameStr
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean) as IntakeFormSection[];
  }, [disclosureHost?.data?.formName]);

  const tabs =
    submittedFormNames.length > 1
      ? submittedFormNames
      : (selectedSection ?? []);
  const showTabs = tabs.length > 1 && _showTabs;

  const [activeTab, setActiveTab] = useState<IntakeFormSection | undefined>(
    selectedSection?.[0] ?? tabs[0]
  );

  useEffect(() => {
    if (disclosureHost.opened && showTabs) {
      const initial = selectedSection?.find((s) => tabs.includes(s));
      setActiveTab(initial ?? tabs[0]);
    }
  }, [disclosureHost.opened, disclosureHost?.data?.formName]);

  const handleReviewRequiringAction = () => {
    showToastOnApiCall({
      apiCall: resolveRequiringAction({
        requiringActionId: resolveRequiringActionId!,
      }),
      showSnackbar,
      onSave: () => {
        queryClient.invalidateQueries({
          queryKey: ["patient-requiring-action"],
        });
        disclosureHost.close();
      },
    });
  };

  const sectionsForView: IntakeFormSection[] | undefined = activeTab
    ? [activeTab]
    : tabs.length > 0
      ? tabs
      : selectedSection;

  return (
    <SideSheetDrawer
      open={disclosureHost.opened}
      onClose={disclosureHost.close}
      title="Form Preview"
      w="60vw"
      footerActions={
        <>
          {resolveRequiringActionId && (
            <Button variant="contained" onClick={handleReviewRequiringAction}>
              Mark As Reviewed
            </Button>
          )}
          <Button variant="contained" onClick={disclosureHost.close}>
            Close
          </Button>
        </>
      }
    >
      {onBack && (
        <Flex
          alignItems="center"
          gap={0.5}
          onClick={onBack}
          sx={{
            cursor: "pointer",
            color: "primary.main",
            mb: 1.5,
            width: "fit-content",
          }}
        >
          <ArrowBackIcon fontSize="small" />
          <Text color="primary.main">{backLabel}</Text>
        </Flex>
      )}

      {showTabs && (
        <Box
          sx={{
            mb: 2,
            width: "100%",
            overflowX: "auto",
            "& > *": { width: "fit-content" },
          }}
        >
          <SegmentTabs
            value={
              activeTab
              // ? { value: activeTab, label: getSectionLabel(activeTab) }
              // : { value: "", label: "" }
            }
            onChange={(v) => setActiveTab(v as IntakeFormSection)}
            tabsData={tabs.map((section) => ({
              value: section,
              label: getSectionLabel(section),
            }))}
          />
        </Box>
      )}

      <ViewPatientIntakeForm
        showOnlyIntakeForms={sectionsForView}
        key={`${disclosureHost?.opened ? "open" : "close"}-${activeTab ?? ""}`}
        prefillData={{
          content: disclosureHost?.data?.content,
          activeSections: disclosureHost?.data?.activeSections,
        }}
      />
    </SideSheetDrawer>
  );
};

export default IntakePreviewModal;
